// src/sim/round/fsm.ts — P3 round FSM pure transition (GAME-01, GAME-02, GAME-08).
//
// Invariants:
//   - Pure: returns { next, events }; never mutates `prev`.
//   - Deterministic: transitions only depend on (prev, caughtCount, currentTick).
//   - Does NOT own ended→headstart — that is flipRolesAndReset() in team-switch.ts
//     because it requires the full WorldState (role swap, entity reset).
//
// Source: .planning/phases/03-core-sim-primitives/03-CONTEXT.md D-06, D-07, D-32.

import type { RoundState } from '../../shared/types/world';
import type { GameEvent } from '../../shared/types/events';
// P7 POL-06 / D-12: round duration read through the tuning cache (./constants is the fallback default).
import { getTuning } from '../content/cache';

export interface FsmResult {
  next: RoundState;
  events: GameEvent[];
}

/**
 * Advance the round FSM one tick.
 *
 * Transition rules (D-06, D-07, D-32):
 *   - headstart → active when currentTick >= prev.headstartEndTick (no event).
 *     round-started is emitted by createInitialWorldState / flipRolesAndReset
 *     at the BEGINNING of the headstart window, not at this boundary.
 *   - active → ended (all-caught) when caughtCount === totalHiders (and totalHiders > 0).
 *     Emits round-ended(endReason='all-caught') + team-switched.
 *   - active → ended (timer) when currentTick >= startTick + P3_ROUND_DURATION_TICKS.
 *     Emits round-ended(endReason='timer') + team-switched.
 *   - When both end conditions fire the same tick, all-caught wins (D-07 order).
 *   - ended → headstart is OWNED BY flipRolesAndReset() in team-switch.ts (requires
 *     full WorldState, not just RoundState). This function stays 'ended' there.
 */
export function advanceRoundFsm(
  prev: RoundState,
  caughtCount: number,
  currentTick: number,
): FsmResult {
  const events: GameEvent[] = [];
  const next: RoundState = { ...prev, caughtCount };

  if (prev.phase === 'headstart' && currentTick >= prev.headstartEndTick) {
    next.phase = 'active';
    return { next, events };
  }

  if (prev.phase === 'active') {
    // Check all-caught first — takes precedence over timer if both fire same tick (D-07).
    if (prev.totalHiders > 0 && caughtCount === prev.totalHiders) {
      next.phase = 'ended';
      next.endReason = 'all-caught';
      next.endTick = currentTick;
      events.push({
        type: 'round-ended',
        roundNumber: prev.roundNumber,
        endReason: 'all-caught',
        caughtCount,
        totalHiders: prev.totalHiders,
        tick: currentTick,
      });
      events.push({ type: 'team-switched', roundNumber: prev.roundNumber, tick: currentTick });
      return { next, events };
    }
    if (currentTick >= prev.startTick + getTuning().roundDurationTicks) {
      next.phase = 'ended';
      next.endReason = 'timer';
      next.endTick = currentTick;
      events.push({
        type: 'round-ended',
        roundNumber: prev.roundNumber,
        endReason: 'timer',
        caughtCount,
        totalHiders: prev.totalHiders,
        tick: currentTick,
      });
      events.push({ type: 'team-switched', roundNumber: prev.roundNumber, tick: currentTick });
      return { next, events };
    }
    return { next, events };
  }

  // phase === 'ended' — no-op here; flipRolesAndReset() handles the headstart flip.
  return { next, events };
}
